import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ModuleLayout from '../common/ModuleLayout';
import SafeIcon from '../../common/SafeIcon';

const { FiLayers, FiPlus, FiTrash2, FiZap, FiBookOpen, FiClipboard, FiInfo } = FiIcons;

const ACTS_CONFIG = [
  { id: 'act1', name: 'Act 1: Setup', purpose: 'Introduce character, world, and inciting incident.' },
  { id: 'act2', name: 'Act 2A: Rising Action', purpose: 'Develop conflict, obstacles, and the "new world".' },
  { id: 'act3', name: 'Act 2B: Midpoint Crisis', purpose: 'A major revelation, setback, or point of no return.' },
  { id: 'act4', name: 'Act 3: Resolution', purpose: 'The final climax and the resolution of the core conflict.' }
];

const INITIAL_FICTION = {
  act1: { content: '', notes: '' },
  act2: { content: '', notes: '' },
  act3: { content: '', notes: '' },
  act4: { content: '', notes: '' }
};

const ModuleTwo = ({ data, onSave, completed }) => {
  const [bookType, setBookType] = useState('nonfiction');
  const [fictionData, setFictionData] = useState(INITIAL_FICTION);
  const [nonfictionChapters, setNonfictionChapters] = useState([
    { chapter: 1, bigIdea: '', biblicalTruth: '', readerShift: '', keyTakeaway: '' }
  ]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) {
      if (data.bookType) setBookType(data.bookType);
      
      // Defensively load fiction data
      if (data.fictionData && typeof data.fictionData === 'object' && !Array.isArray(data.fictionData)) {
        setFictionData(prev => ({
          ...prev,
          ...data.fictionData
        }));
      }
      
      if (Array.isArray(data.nonfictionChapters) && data.nonfictionChapters.length > 0) {
        setNonfictionChapters(data.nonfictionChapters);
      }
    }
  }, [data]);

  const handleFictionChange = (actKey, field, value) => {
    setFictionData(prev => ({
      ...prev,
      [actKey]: { 
        ...(prev[actKey] || { content: '', notes: '' }), 
        [field]: value 
      }
    }));
  };

  const handleChapterChange = (index, field, value) => {
    setNonfictionChapters(prev => prev.map((chapter, i) => 
      i === index ? { ...chapter, [field]: value } : chapter
    ));
  };

  const addChapter = () => {
    setNonfictionChapters(prev => [...prev, { 
      chapter: prev.length + 1, bigIdea: '', biblicalTruth: '', readerShift: '', keyTakeaway: '' 
    }]);
  };

  const removeChapter = (index) => {
    if (nonfictionChapters.length > 1) {
      setNonfictionChapters(prev => prev.filter((_, i) => i !== index).map((ch, idx) => ({ ...ch, chapter: idx + 1 })));
    }
  };

  const handleSave = async (markComplete) => {
    setIsLoading(true);
    const dataToSave = { bookType, fictionData, nonfictionChapters };
    setTimeout(() => {
      onSave(dataToSave, markComplete);
      setIsLoading(false);
    }, 800);
  };

  // Robust validation to prevent "white screen" crashes
  const canComplete = bookType === 'fiction' 
    ? Object.values(fictionData || {}).some(act => act && typeof act.content === 'string' && act.content.trim() !== '')
    : Array.isArray(nonfictionChapters) && nonfictionChapters.some(ch => ch && typeof ch.bigIdea === 'string' && ch.bigIdea.trim() !== '');

  return (
    <ModuleLayout
      title="Structure the Story or Message"
      description="Create a clear roadmap for your book so momentum replaces overwhelm."
      icon={FiLayers}
      onSave={handleSave}
      completed={completed}
      canComplete={canComplete}
      isLoading={isLoading}
    >
      <div className="space-y-8">
        {/* Step 1: Selector */}
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
          <label className="block text-sm font-bold text-slate-400 uppercase tracking-wider mb-6">Step 1: Book Type</label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setBookType('fiction')}
              className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center space-y-3 ${bookType === 'fiction' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'}`}
            >
              <SafeIcon icon={FiZap} className="text-2xl" />
              <div className="text-center font-bold text-sm sm:text-base">Fiction Path</div>
            </button>
            <button
              onClick={() => setBookType('nonfiction')}
              className={`p-6 rounded-2xl border-2 transition-all flex flex-col items-center space-y-3 ${bookType === 'nonfiction' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-100 bg-slate-50 text-slate-400 hover:border-slate-200'}`}
            >
              <SafeIcon icon={FiBookOpen} className="text-2xl" />
              <div className="text-center font-bold text-sm sm:text-base">Nonfiction Path</div>
            </button>
          </div>
        </div>

        {/* Path Content */}
        <AnimatePresence mode="wait">
          {bookType === 'fiction' ? (
            <motion.div key="fiction" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
              <div className="flex items-center space-x-2 text-blue-600 mb-2 px-2">
                <SafeIcon icon={FiInfo} />
                <span className="text-[10px] font-bold uppercase tracking-widest">StoryWise 4-Act Structure</span>
              </div>
              {ACTS_CONFIG.map((act) => (
                <div key={act.id} className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
                  <div className="mb-4">
                    <h4 className="text-xl font-black text-slate-800">{act.name}</h4>
                    <p className="text-sm text-slate-500 font-medium italic">{act.purpose}</p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">What happens in this act?</label>
                      <textarea
                        value={fictionData[act.id]?.content || ''}
                        onChange={(e) => handleFictionChange(act.id, 'content', e.target.value)}
                        rows={4}
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none"
                        placeholder="Key plot points, character growth..."
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Optional Notes</label>
                      <input
                        type="text"
                        value={fictionData[act.id]?.notes || ''}
                        onChange={(e) => handleFictionChange(act.id, 'notes', e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                        placeholder="Themes, sensory ideas, or specific dialogue..."
                      />
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div key="nonfiction" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
              {nonfictionChapters.map((chapter, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-sm p-8 border border-slate-100">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-xl font-black text-slate-800">Chapter {chapter.chapter}</h4>
                    {nonfictionChapters.length > 1 && (
                      <button onClick={() => removeChapter(index)} className="text-red-400 hover:text-red-600 p-2 transition-colors">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Big Idea</label>
                      <input
                        type="text"
                        value={chapter.bigIdea}
                        onChange={(e) => handleChapterChange(index, 'bigIdea', e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-bold text-slate-700"
                        placeholder="The core message of this chapter"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Biblical Truth</label>
                      <input
                        type="text"
                        value={chapter.biblicalTruth}
                        onChange={(e) => handleChapterChange(index, 'biblicalTruth', e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                        placeholder="Scripture or gospel principle"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Reader Shift</label>
                      <input
                        type="text"
                        value={chapter.readerShift}
                        onChange={(e) => handleChapterChange(index, 'readerShift', e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                        placeholder="From [Struggle] to [Hope]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Key Takeaway</label>
                      <input
                        type="text"
                        value={chapter.keyTakeaway}
                        onChange={(e) => handleChapterChange(index, 'keyTakeaway', e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                        placeholder="What should the reader DO after this chapter?"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button 
                onClick={addChapter}
                className="w-full py-6 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 hover:text-blue-500 hover:border-blue-200 hover:bg-blue-50 transition-all flex items-center justify-center space-x-2 font-bold"
              >
                <SafeIcon icon={FiPlus} />
                <span>Add Another Chapter</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Book Map Overview (Auto Summary) */}
        {(canComplete) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900 rounded-3xl p-8 text-white shadow-2xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-white/10 rounded-lg">
                <SafeIcon icon={FiClipboard} />
              </div>
              <h3 className="text-xl font-bold">Book Map Overview</h3>
            </div>
            <div className="bg-white/5 rounded-2xl p-6 border border-white/10 overflow-hidden">
              {bookType === 'fiction' ? (
                <div className="space-y-4">
                  {ACTS_CONFIG.map(act => (
                    <div key={act.id} className="flex space-x-4">
                      <div className="font-bold text-blue-400 w-24 flex-shrink-0 text-xs sm:text-sm">{act.id.toUpperCase()}</div>
                      <div className="text-slate-300 text-xs sm:text-sm italic truncate">
                        {fictionData[act.id]?.content ? fictionData[act.id].content : 'Pending...'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {nonfictionChapters.map(ch => (
                    <div key={ch.chapter} className="flex space-x-4">
                      <div className="font-bold text-orange-400 w-24 flex-shrink-0 text-xs sm:text-sm">CH {ch.chapter}</div>
                      <div className="text-slate-300 text-xs sm:text-sm truncate">
                        <span className="font-bold text-white uppercase text-[10px] mr-2">IDEA:</span>
                        {ch.bigIdea || 'Pending...'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-4 text-center uppercase tracking-widest">Auto-generated summary for quick reference</p>
          </motion.div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default ModuleTwo;